import React, { useEffect, useState } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import moment from 'moment-timezone'
import { string, object, date } from 'yup'
import {
  useErrorService,
  ABSpinner,
  useNotificationService,
  useUserService,
  sows,
} from '@abroad/components'
import { AddClientTeamSession } from '../components'
import API from '../utils/API'
import { getLoginUserCoachingPlan } from '../utils/utility'
import { sowStatus } from '@abroad/components/src/helper/common'

export const AddSession = () => {
  const validationSchema = object().shape({
    planId: string().required(),
    sessionDuration: string().required(),
    sessionDate: date()
      .required()
      .test('valid-date', 'Please enter valid date!', function (value) {
        const checkDate = moment(value, 'MM/DD/YYYY')
        return checkDate.isValid()
      }),
  })

  const [isLoading, setIsLoading] = useState(true)
  const [isSessionCreateLoading, setIsSessionCreateLoading] = useState(false)
  const [sessionInsights, setSessionInsights] = useState([])
  const [nonConfidentialsessionInsights, setNonConfidentialSessionInsights] =
    useState([])
  const [postSession, setPostSession] = useState('')
  const [search, setSearch] = useState('')
  const [searchKeySkill, setSearchKeySkill] = useState('')
  const [searchData, setSearchData] = useState([])
  const [searchKeySkillData, setSearchKeySkillData] = useState([])
  const [inputFocus, setInputFocus] = useState(false)
  const [inputFocusOfKeySkill, setInputFocusOfKeySkill] = useState(false)
  const [isSearch, setIsSearch] = useState(false)
  const [isSearchKeySkill, setIsSearchKeySkill] = useState(false)
  const [clientInfo, setClientInfo] = useState([])
  const Error = useErrorService()
  const { user } = useUserService()
  const Notification = useNotificationService()
  const history = useHistory()
  const { clientId } = useParams()
  const location = useLocation()
  const [keySkillSearchData, setKeySkillSearchData] = useState([])
  const [coachingPlans, setCoachingPlans] = useState([])
  const [scheduledEvents, setScheduledEvents] = useState([])
  const [isLoadingScheduledEvents, setIsLoadingScheduledEvents] =
    useState(false)
  const [formInitialValues, setFormInitialValues] = useState({
    sessionDate: null,
    sessionDuration: '',
    planId: '',
    scheduledEventId: '',
  })
  const key = 'includeDeleted'

  useEffect(() => {
    const getCoachingPlans = async (id = null) => {
      try {
        const { data } = await API.AdminAPI.coachPlan.getClientsCoachingPlans(
          clientId,
          `?${key}=true`,
        )

        if (data) {
          const filteredData = data.filter((e) => {
            // First check: skip if remainingSession is 0
            if (e.remainingSession === 0) {
              return false
            }

            // Then apply the existing status-based filtering
            return (
              e.sowStatus === sowStatus.IN_PROGRESS ||
              e.sowStatus === sowStatus.PAYMENT_FAILED ||
              (e.sowStatus === sowStatus.TERMINATED &&
                (e.remainingSession > 0 ||
                  (e.billingIndefinite === true &&
                    moment(e.terminatedOn).isAfter(moment()))))
            )
          })
          const updatedCoachingPlans = getLoginUserCoachingPlan(
            filteredData,
            user?._id,
          )
          // Keep existing status/remaining-session filtering above.
          // If navigated with a selected plan (overview modal or booking list),
          // ensure that plan appears in the Plan dropdown without loosening filters.
          let planOptions = updatedCoachingPlans?.labelValuesData || []
          const selectedPlanFromState = location.state?.selectedPlan
          const scheduledEventIdFromState =
            location.state?.scheduledEventId ||
            location.state?.scheduleEventId ||
            null

          if (selectedPlanFromState?._id) {
            const planAlreadyListed = planOptions.some(
              (plan) => plan.value === selectedPlanFromState._id,
            )
            if (!planAlreadyListed) {
              planOptions = [
                ...planOptions,
                {
                  label: selectedPlanFromState.planName || 'Selected Plan',
                  value: selectedPlanFromState._id,
                  type: sows.planType.individual,
                  sessionDuration: selectedPlanFromState.sessionDuration,
                },
              ]
            }
          }

          setCoachingPlans(planOptions)

          let tempInitialValues = { ...formInitialValues }

          if (selectedPlanFromState?._id) {
            // Prefer SOW sessionDuration from filtered coaching plans when available
            const matchedPlanOption = planOptions.find(
              (plan) => plan.value === selectedPlanFromState._id,
            )
            const matchedPlanData =
              updatedCoachingPlans?.coachingPlanData?.find(
                (plan) => plan._id === selectedPlanFromState._id,
              ) || null

            tempInitialValues.planId = selectedPlanFromState._id
            const resolvedDuration =
              matchedPlanData?.sessionDuration ||
              matchedPlanOption?.sessionDuration ||
              selectedPlanFromState.sessionDuration
            if (resolvedDuration !== undefined && resolvedDuration !== null) {
              tempInitialValues.sessionDuration = String(resolvedDuration)
            }

            // Seed calendar event id early so Formik can prefill after events load
            if (scheduledEventIdFromState) {
              tempInitialValues.scheduledEventId = String(
                scheduledEventIdFromState,
              )
            }
          } else if (planOptions.length === 1) {
            // Auto-select if only one plan available
            tempInitialValues.planId = planOptions[0]?.value
            if (updatedCoachingPlans?.coachingPlanData[0]?.sessionDuration) {
              tempInitialValues.sessionDuration = String(
                updatedCoachingPlans.coachingPlanData[0].sessionDuration,
              )
            }
          }

          setFormInitialValues(tempInitialValues)
        }
        setIsLoading(false)
      } catch (e) {
        setIsLoading(false)
        Error.showError(e)
      }
    }
    const getUserInfo = async () => {
      try {
        const { data } = await API.AdminAPI.coachClient.getClientSpecificData(
          clientId,
          {
            fields: ['fullName', 'coachIds', 'planCode', 'fname'],
          },
        )
        if (data) {
          const allCoachIds = data?.coachIds?.map((coach) => coach?._id)
          setClientInfo(data)
          if (!allCoachIds?.includes(user?._id)) {
            history.push('/')
          } else {
            getCoachingPlans()
          }
        }
      } catch (e) {
        setIsLoading(false)
        Error.showError(e)
      }
    }
    getUserInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const getSearchData = async () => {
      setIsSearch(true)
      try {
        if (search !== '') {
          const { data } = await API.AdminAPI.common.getSearchMedia(
            search,
            'user',
            clientId,
          )
          if (data) {
            setSearchData(data)
            setIsSearch(false)
          }
        }
      } catch (e) {
        Error.showError(e)
      }
    }
    getSearchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    const getSearchData = () => {
      setIsSearchKeySkill(true)
      const data = keySkillSearchData.filter((data) =>
        data?.title?.toLowerCase()?.includes(searchKeySkill?.toLowerCase()),
      )

      setSearchKeySkillData(data)
      setIsSearchKeySkill(false)
    }
    getSearchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeySkill, keySkillSearchData])

  useEffect(() => {
    const getKeySkillData = async () => {
      try {
        const { data } = await API.AdminAPI.common.getKeySkillSearchPdf()
        if (data) {
          const keySkillsData = data.map((keySkill) => ({
            _id: keySkill?._id,
            title: keySkill?.keySkill?.title,
            description: keySkill?.keySkill?.description,
          }))
          setKeySkillSearchData(keySkillsData)
        }
      } catch (e) {
        Error.showError(e)
      }
    }
    getKeySkillData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getScheduledEventOptionId = (event) =>
    event?._id ||
    event?.scheduleEventId ||
    event?.scheduledEventId ||
    event?.id ||
    null

  const buildBookingListCalendarOption = () => {
    const scheduledEventIdFromState =
      location.state?.scheduledEventId ||
      location.state?.scheduleEventId ||
      null
    if (!scheduledEventIdFromState) return null

    const selectedEventFromState = location.state?.scheduledEvent
    let label = selectedEventFromState?.title || 'Selected calendar event'
    if (selectedEventFromState?.startDateTime) {
      const formattedDate = moment(selectedEventFromState.startDateTime).format(
        'MM/DD/YYYY',
      )
      label = `${formattedDate}-${selectedEventFromState.title || 'Event'}`
    }

    return {
      label,
      value: String(scheduledEventIdFromState),
    }
  }

  const fetchScheduledEvents = async (sowId) => {
    if (!sowId) {
      setScheduledEvents([])
      return
    }
    setIsLoadingScheduledEvents(true)
    const fromScheduledBooking = !!location.state?.fromScheduledBooking
    const scheduledEventIdFromState =
      location.state?.scheduledEventId ||
      location.state?.scheduleEventId ||
      null
    const bookingListOption = buildBookingListCalendarOption()

    try {
      // Keep sessionType=coaching restriction for regular session logging
      const { data } = await API.AdminAPI.coachPlan.getScheduledEvents(sowId, {
        sessionType: 'coaching',
      })
      if (data && Array.isArray(data)) {
        const formattedEvents = data
          .map((event) => {
            const eventId = getScheduledEventOptionId(event)
            if (!eventId) return null
            const formattedDate = moment(event.startTime).format('MM/DD/YYYY')
            return {
              label: `${formattedDate}-${event.title}`,
              value: String(eventId),
            }
          })
          .filter(Boolean)

        // From booking list: show only the selected scheduled event (not all SOW events)
        if (fromScheduledBooking && scheduledEventIdFromState) {
          const matchedFromApi = formattedEvents.find((event) => {
            // Match option value that corresponds to any id form of the booking event
            return String(event.value) === String(scheduledEventIdFromState)
          })
          const matchedRaw = data.find((event) => {
            const ids = [
              event?._id,
              event?.scheduleEventId,
              event?.scheduledEventId,
              event?.id,
            ]
              .filter(Boolean)
              .map(String)
            return ids.includes(String(scheduledEventIdFromState))
          })
          const matchedOptionId = getScheduledEventOptionId(matchedRaw)
          const onlySelectedEvent =
            matchedFromApi ||
            (matchedOptionId
              ? {
                  label: `${moment(matchedRaw.startTime).format(
                    'MM/DD/YYYY',
                  )}-${matchedRaw.title}`,
                  value: String(matchedOptionId),
                }
              : null) ||
            bookingListOption

          setScheduledEvents(onlySelectedEvent ? [onlySelectedEvent] : [])
          if (onlySelectedEvent) {
            setFormInitialValues((prev) => ({
              ...prev,
              scheduledEventId: String(onlySelectedEvent.value),
            }))
          }
        } else {
          // Overview / normal flow: keep full event list from API
          setScheduledEvents(formattedEvents)
        }
      } else if (fromScheduledBooking && bookingListOption) {
        // API empty, but we still have the booking-list event to show
        setScheduledEvents([bookingListOption])
        setFormInitialValues((prev) => ({
          ...prev,
          scheduledEventId: bookingListOption.value,
        }))
      } else {
        setScheduledEvents([])
      }
      setIsLoadingScheduledEvents(false)
    } catch (e) {
      // From booking list, still show the selected event if API fails
      if (fromScheduledBooking && bookingListOption) {
        setScheduledEvents([bookingListOption])
        setFormInitialValues((prev) => ({
          ...prev,
          scheduledEventId: bookingListOption.value,
        }))
      } else {
        setScheduledEvents([])
      }
      setIsLoadingScheduledEvents(false)
      // Don't show error for scheduled events as it's optional
    }
  }

  useEffect(() => {
    if (formInitialValues.planId) {
      fetchScheduledEvents(formInitialValues.planId)
    } else {
      setScheduledEvents([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formInitialValues.planId])

  if (isLoading) {
    return <ABSpinner />
  }

  const onSubmit = async (values) => {
    if (sessionInsights.length === 0) {
      Notification.showNotification('Please Enter Session Insights!', 'danger')
    } else {
      const filterSelectedPlan = coachingPlans?.filter(
        (plan) => plan?.value === values?.planId,
      )
      const isIndividualPlanSelected =
        filterSelectedPlan[0]?.type === sows.planType.individual
      let params = {
        date: moment(values.sessionDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        length: values.sessionDuration,
        coachNotes: sessionInsights,
        postSession: postSession,
        coachNotesNonConfidential:
          nonConfidentialsessionInsights.length === 0
            ? ''
            : nonConfidentialsessionInsights,
        planType: isIndividualPlanSelected
          ? sows.planType.individual
          : sows.planType.b2b,
      }
      if (values?.keySkillsIds?.length > 0) {
        params.keySkillIds = values.keySkillsIds
      }
      if (values.mediaIds.length > 0) {
        params.mediaIds = values.mediaIds
      }
      if (values.moduleIds.length > 0) {
        params.moduleIds = values.moduleIds
      }
      if (values.planId) {
        params.planId = values.planId
      }
      if (values.scheduledEventId) {
        params.scheduledEventId = values.scheduledEventId
      }
      Object.keys(params).forEach(
        (key) => params[key] === '' && delete params[key],
      )
      try {
        setIsSessionCreateLoading(true)
        const { data } = await API.CoachAPI.coachClient.sessionCreate(
          clientId,
          params,
        )
        if (data) {
          setIsSessionCreateLoading(false)
          Notification.showNotification('session has been created!', 'success')
          history.goBack()
        }
      } catch (e) {
        if (e?.code === 'session_limit_exceeded') {
          setIsSessionCreateLoading(false)
        } else {
          setIsSessionCreateLoading(false)
          Error.showError(e)
        }
      }
    }
  }

  return (
    <>
      <Helmet>
        <title>{`${clientInfo?.fname}'s Session | Abroad`}</title>
        <meta
          name='title'
          content={`${clientInfo?.fname}'s Session | Abroad`}></meta>
        <meta
          property='og:title'
          content={`${clientInfo?.fname}'s Session | Abroad`}></meta>
        <meta
          property='twitter:title'
          content={`${clientInfo?.fname}'s Session | Abroad`}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/coach/clients/${clientId}/session`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/coach/clients/${clientId}/session`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/coach/clients/${clientId}/session`}></meta>
      </Helmet>

      <AddClientTeamSession
        validationSchema={validationSchema}
        formInitialValues={formInitialValues}
        onSubmit={onSubmit}
        titleText={`Add Client Session For ${clientInfo?.fullName}`}
        sessionInsights={sessionInsights}
        setSessionInsights={setSessionInsights}
        nonConfidentialsessionInsights={nonConfidentialsessionInsights}
        setNonConfidentialSessionInsights={setNonConfidentialSessionInsights}
        postSession={postSession}
        setPostSession={setPostSession}
        search={search}
        setSearch={setSearch}
        inputFocus={inputFocus}
        setInputFocus={setInputFocus}
        searchData={searchData}
        isSearch={isSearch}
        isSessionCreateLoading={isSessionCreateLoading}
        searchKeySkill={searchKeySkill}
        setSearchKeySkill={setSearchKeySkill}
        searchKeySkillData={searchKeySkillData}
        isSearchKeySkill={isSearchKeySkill}
        inputFocusOfKeySkill={inputFocusOfKeySkill}
        setInputFocusOfKeySkill={setInputFocusOfKeySkill}
        coachingPlans={coachingPlans}
        scheduledEvents={scheduledEvents}
        isLoadingScheduledEvents={isLoadingScheduledEvents}
        onPlanChange={fetchScheduledEvents}
        // Lock plan + calendar when coming from booking list (already chosen there)
        disablePlanSelection={!!location.state?.fromScheduledBooking}
        disableCalendarSelection={!!location.state?.fromScheduledBooking}
      />
    </>
  )
}
